import { Action, Dispatch } from 'redux';
import { IAppState } from '../reducers';
import {
  ActionType as FormAction,
  updateFormType, FormType,
  updateSingleSchedule,
} from './gameScheduleForm';
import {
  IExchangeAssetHit,
  searchExchangeAssets,
  defaultExchangeSearchParams,
} from '../../api/vault/searchEngine';
import { IExchangeSchedulePayload, updateSchedule as updateScheduleApi } from '../../api/vault/exchangeSchedule';
import toast from '../../utils/toast';

export interface IDeleteDialogState {
  isOpen: boolean;
  isLoading: boolean;
  isLoaded: boolean;
  assets: Array<IExchangeAssetHit>;
}

export interface IState {
  isOpen: boolean;
  deleteDialog: IDeleteDialogState;
  isUpdating: boolean;
}

export enum ActionType {
  Toggle = 'UpdateGameScheduleDialog.Toggle',
  ClearAll = 'UpdateGameScheduleDialog.ClearAll',
  DeleteDialogToggle = 'UpdateGameScheduleDialog.DeleteDialog.Toggle',
  DeleteDialogToggleLoading = 'UpdateGameScheduleDialog.DeleteDialog.ToggleLoading',
  DeleteDialogUpdateAssets = 'UpdateGameScheduleDialog.DeleteDialog.UpdateAssets',
  UpdateToggleUpdating = 'UpdateGameScheduleDialog.UpdateToggleUpdating',
}

export interface IUpdateGameScheduleDialogAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  deleteDialog: {
    isOpen: false,
    isLoading: false,
    isLoaded: false,
    assets: [],
  },
  isUpdating: false,
};

export default function reducer(state: IState = INITIAL_STATE, action: IUpdateGameScheduleDialogAction): IState {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.ClearAll:
      return {
        ...INITIAL_STATE,
      };
    case ActionType.DeleteDialogToggle:
      return {
        ...state,
        deleteDialog: {
          ...state.deleteDialog,
          isOpen: !state.deleteDialog.isOpen,
        },
      };
    case ActionType.DeleteDialogToggleLoading:
      return {
        ...state,
        deleteDialog: {
          ...state.deleteDialog,
          isLoading: !state.deleteDialog.isLoading,
        },
      };
    case ActionType.DeleteDialogUpdateAssets:
      return {
        ...state,
        deleteDialog: {
          ...state.deleteDialog,
          assets: action.payload.assets,
          isLoaded: true,
        },
      };
    case ActionType.UpdateToggleUpdating:
      return {
        ...state,
        isUpdating: !state.isUpdating,
      };
    default:
      return state;
  }
}

export const toggleUpdateGameScheduleDialog = () => (dispatch: Dispatch, getState: () => IAppState) => {
  const {
    exchange: {
      updateGameScheduleDialog: {
        isOpen,
      },
    },
  } = getState();
  if (!isOpen) dispatch(updateFormType(FormType.Update));
  dispatch({ type: ActionType.Toggle });
};

export const updateSchedule = (payloadTemp: IExchangeSchedulePayload) => async (dispatch: Dispatch, getState: () => IAppState) => {
  const payload: IExchangeSchedulePayload = { ...payloadTemp };
  const {
    exchange: {
      gameScheduleForm: {
        selectedSchedule: {
          _id,
          HomeTeamCode,
          VisitorTeamCode,
          GameDate,
          Sport,
          League,
        },
      },
    },
  } = getState();

  dispatch({ type: ActionType.UpdateToggleUpdating });

  await new Promise((resolve) => setTimeout(resolve, 5000)); // Simulate loading time for better UX

  const {
    Sport: selectedSport,
    League: selectedLeague,
  } = payload;

  try {
    if (Sport !== selectedSport || League !== selectedLeague) {
      const { data: { data: { hits: { hits: assets } } } } = await searchExchangeAssets({
        ...defaultExchangeSearchParams,
        filters: [
          ['homeTeamCode', [HomeTeamCode]],
          ['awayTeamCode', [VisitorTeamCode]],
          ['gameDate', [GameDate]],
        ],
      });
      if (assets.length > 0) {
        toast('Assets have been associated with this game, cannot update sport or league properties.', { type: toast.TYPE.WARNING });
        dispatch({ type: ActionType.UpdateToggleUpdating });
        return;
      }
      payload.Sport = selectedSport;
      payload.League = selectedLeague;
    }
    const response = await updateScheduleApi(_id, payload);
    if (response.status === 200) {
      toast('Game updated successfully', { type: toast.TYPE.INFO });
      updateSingleSchedule(response.data.data)(dispatch, getState);
    }
  } catch (e) {
    console.error('[QA] Error updating game schedule', e.toString());
    toast('Game update failed', { type: toast.TYPE.ERROR });
  }

  dispatch({ type: ActionType.UpdateToggleUpdating });
};

export const clearAll = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  dispatch({
    type: FormAction.ClearAll,
  });

  dispatch({
    type: ActionType.ClearAll,
  });
};

export const toggleDeleteGameDialog = () => ({
  type: ActionType.DeleteDialogToggle,
});

export const toggleDeleteGameDialogLoading = () => ({
  type: ActionType.DeleteDialogToggleLoading,
});

export const loadGameAssets = () => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();

  const {
    exchange: {
      gameScheduleForm: {
        selectedSchedule: {
          GameDate,
          HomeTeamCode,
          VisitorTeamCode,
        },
      },
    },
  } = state;

  dispatch({
    type: ActionType.DeleteDialogToggleLoading,
  });

  try {
    const { data: { data: { hits: { hits: assets } } } } = await searchExchangeAssets({
      ...defaultExchangeSearchParams,
      filters: [
        ['homeTeamCode', [HomeTeamCode]],
        ['awayTeamCode', [VisitorTeamCode]],
        ['gameDate', [GameDate]],
      ],
    });

    dispatch({
      type: ActionType.DeleteDialogUpdateAssets,
      payload: {
        assets,
      },
    });
  } catch (e) {
    console.error('[QA] Error occurred while fetching game assets: ', e.toString());
  }

  dispatch({
    type: ActionType.DeleteDialogToggleLoading,
  });
};
